import {
  ChangeEvent,
  useReducer,
  forwardRef,
  MutableRefObject,
  Ref,
  useEffect,
} from "react";
import cx from "classnames";
import InputMask from "react-input-mask";
import { useFormContext } from "react-hook-form";

import { getError, isFunction } from "@/utils";
import ErrorText from "../ErrorText";
import Text from "../Text";

type InputProps = JSX.IntrinsicElements["input"] & {
  name: string;
  label?: string;
  placeholder?: string;
  mask?: string[] | string;
  maskPlaceholder?: string | null;
  className?: string;
  loading?: boolean;
  error?: string;
  helperText?: string;
  helperTextWithAction?: React.ComponentType;
};

/**
 * Para manter um padrão, foi feita uma função
 * para a utilização das classes do input em outros componentes
 */
export const inputClassName = (state?: "idle" | "error" | "icon") => {
  switch (state) {
    case "idle":
      return "formInput text-gray-600 border-gray-300 text-tim-primary focus:border-tim-primary";
    case "error":
      return "formInput--error text-red-600 border-red-500 hover:border-red-600 focus:border-red-600";
    default:
      return "w-full transition duration-200 border text-sm rounded-xl h-12 px-4 focus:outline-none bg-white";
  }
};

const Input = (
  {
    name,
    type = "text",
    label,
    placeholder,
    mask,
    maskPlaceholder = "",
    className,
    helperText,
    value,
    defaultValue,
    disabled,
    loading,
    helperTextWithAction: HelperTextWithAction,
    onChange,
    onBlur,
    error: inputedError,
    ...rest
  }: InputProps,
  ref: Ref<HTMLInputElement>
) => {
  const [, forceRender] = useReducer((c) => c + 1, 0);

  const {
    register,
    formState: { errors = {} },
    getValues,
    setValue,
  } = useFormContext() || {};
  const error = getError(errors, name) || inputedError;
  const errorID = `error-${name}`;

  const classNames = cx(
    inputClassName(),
    inputClassName(error ? "error" : "idle"),
    {
      "border-tim-primary": loading,
      "formInput--hasContent":
        (getValues?.(name) || value || defaultValue) && !disabled,
      "hover:border-tim-primary": !disabled,
      "formInput--disabled text-gray-500": disabled,
    }
  );
  const wrapperClassNames = cx(className, "relative flex flex-col-reverse");
  const labelClassNames = cx(
    "leading-none pointer-events-none pt-px mb-2 transition duration-200 origin-top-left bg-white px-2 text-sm",
    {
      "text-gray-500 formInput__label__disabled": disabled,
      "formInput__label text-tim-primary": !disabled,
    }
  );

  useEffect(() => {
    setValue(name, value);
  }, [name, setValue, value]);
  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled || loading) {
      return;
    }
    onChange?.(event);
  };

  return (
    <>
      <div>
        <div className={wrapperClassNames}>
          <InputMask
            id={name}
            name={name}
            mask={mask}
            // @ts-expect-error - maskChar is not in the types
            maskChar={maskPlaceholder}
            type={type}
            disabled={disabled || loading}
            className={classNames}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ref={(node: any) => {
              const inputElement = node ? node.inputElement : null;
              register?.(inputElement || name); // Registra o input real
              if (isFunction(ref)) {
                ref(inputElement);
              } else if (ref) {
                (ref as MutableRefObject<HTMLInputElement>).current =
                  inputElement;
              }
            }}
            value={value}
            defaultValue={defaultValue}
            aria-label={placeholder}
            placeholder={placeholder}
            aria-invalid={error ? "true" : "false"}
            data-describedby={errorID}
            onChange={handleOnChange}
            onBlur={(event) => {
              // Force render on blur to get values consistently
              forceRender();
              onBlur?.(event);
            }}
            {...rest}
          />
          <label htmlFor={name} className={labelClassNames}>
            {label || placeholder}
          </label>
          {loading && (
            <div className="formInput__loaderContainer absolute bottom-0 flex h-12 items-center text-tim-primary">
              <i className="formInput__loader" />
            </div>
          )}
        </div>
      </div>
      {!error && helperText && (
        <Text kind="indigo" size="xs" className="px-1 pt-1">
          {helperText}
        </Text>
      )}
      {error && <ErrorText id={errorID}>{error}</ErrorText>}
      {HelperTextWithAction && <HelperTextWithAction />}
    </>
  );
};

export default forwardRef(Input);
